<template lang="pug">
.c-comments
  vue-user-comment-form(
    ref='postForm',
    type='post',
    :placeholder='__("Add comments...")',
    :rows='2',
    :buttons='{submit: {value: __("Post")}, login: {value: type === "projects_backer_only" ? __("Post (Backers only)") : __("Log in to Post") }}'
  )
  p(v-if="state.errors", v-text="state.errors.general", style="color: #e30920")
  div(:class="{ 'c-loading': isLoading, 'u-mtm': isLoading || (state.comments && state.comments.length) }", :style="[loadingStyleObject]")
    vue-user-comment-threads(:comment='comment', v-for='comment in state.comments', :type='type', :key="comment.id")
    .u-align-center.u-mtl(v-if="state.hasNext")
      button.c-btn.c-btn--primary.c-btn--inverse.c-btn--fill(@click.prevent="clickSeeMore")
        | {{__("See more comments")}}
  vue-recaptcha(
    ref="recaptcha"
    size="invisible"
    :sitekey="recaptchaSiteKey"
    @verify="onCaptchaVerified"
    @expired="onCaptchaExpired"
    @error="onCaptchaError",
    v-if="recaptchaSiteKey"
  )
</template>

<script>
let userCommentsBus;
import Vue from 'vue';

window.vueEventBus = window.vueEventBus || {};
window.vueEventBus.userComments = userCommentsBus = new Vue();

import { userCommentsStore } from '../../stores/UserComments';
import userCommentForm from './userCommentForm.vue';
import userCommentThreads from './userCommentThreads.vue';

import { I18n } from '../../util/mixin/i18n.mixin';
import { LoadingStatus } from '../../util/mixin/loadingStatus.mixin';
import { VueRecaptcha } from 'vue-recaptcha';

export default {
  mixins: [LoadingStatus, I18n],
  components: {
    'vue-user-comment-form': userCommentForm,
    'vue-user-comment-threads': userCommentThreads,
    'vue-recaptcha': VueRecaptcha
  },
  props: ['url', 'type', 'embedId', 'projectId', 'recaptchaSiteKey'],

  data() {
    return {
      state: userCommentsStore.getState(),
      recaptcha: {
        pendingResolver: null,
        pendingRejecter: null,
        isExecuting: false
      }
    };
  },

  computed: {
    isIframe() {
      return window.parent !== window.self;
    },
    isLogin() {
      return userCommentsStore.isUserLogin();
    },
    canPost() {
      return userCommentsStore.canPost();
    },
    loadingStyleObject() {
      let minHeightValue = 0;
      if (this.isLoading) {
        minHeightValue = '4em';
      }
      return {
        minHeight: minHeightValue
      };
    }
  },

  created() {
    this.$on('LOAD', () => {
      this.loadStart();
      userCommentsBus.$emit('CHANGE_HEIGHT');
    });

    this.$on('UPDATED', () => {
      this.loadEnd();
      // コメント投稿や削除時に高さが変化するので親に伝える
      userCommentsBus.$emit('CHANGE_HEIGHT');
      if (this.state.success != null ? this.state.success.message : undefined) {
        alert(this.state.success.message);
      }
      // iframe内のコンテンツの場合は親ウインドウにコメント数を渡す
      if (this.isIframe) {
        const userComments =
          window.parent.tom &&
          window.parent.tom.widget &&
          window.parent.tom.widget.userComments;
        if (userComments) {
          userComments.updateCount(this.state.commentCount);
        }
      }
      // Cumulative Layout Shift対策のためコメントの表示後にfooterを表示する。
      const footer = document.querySelector('footer.p-global-footer');
      if (footer) {
        footer.removeAttribute('style');
      }

      // VueのDOM更新後、ブラウザのレイアウト確定まで時間がかかる場合があるため、
      // 少し待ってから高さを更新する。
      setTimeout(() => {
        userCommentsBus.$emit('CHANGE_HEIGHT');
      }, 500);
    });

    this.$on('FAILED', () => {
      this.loadEnd();
      // コメント投稿や削除時に高さが変化するので親に伝える
      userCommentsBus.$emit('CHANGE_HEIGHT');
    });

    userCommentsBus.$on('CHANGE_HEIGHT', (documentHeight) => {
      if (documentHeight == null) {
        documentHeight = 0;
      }
      this.$nextTick(function () {
        // iframe内のコンテンツの場合はwidgetにheightを設定するメソッドを叩く
        if (this.isIframe) {
          const userComments =
            window.parent.tom &&
            window.parent.tom.widget &&
            window.parent.tom.widget.userComments;
          if (userComments) {
            userComments.adjustHeight(this.$el, this.embedId, documentHeight);
          }
        }
      });
    });
  },

  watch: {
    state: {
      handler(state) {
        this.$nextTick(function () {
          this.$emit(state.loadingStatus);
          this.$root.$emit(state.loadingStatus);
        });
      },
      deep: true
    }
  },

  mounted() {
    const options = {
      limit: 10
    };
    if (this.type) {
      options.type = this.type;
    }
    if (this.projectId) {
      options.project = this.projectId;
    }

    userCommentsBus.$emit('CHANGE_HEIGHT');

    if (window.userCommentProvider && window.userCommentProvider.provider) {
      window.userCommentProvider.setResolver((data) => {
        userCommentsStore.setThreads(null, data);
      });
    } else {
      userCommentsStore.getThreads(this.url, options);
    }

    this.$refs.postForm.$on('submit', (options) => {
      userCommentsStore.postComment(
        this.state.thread._id,
        this.$refs.postForm.comment,
        options
      );
    });
  },

  provide() {
    return {
      requestRecaptcha: this.executeRecaptcha
    };
  },

  methods: {
    clickSeeMore() {
      const options = {
        limit: 10,
        last: this.state.comments[this.state.comments.length - 1]._id
      };
      if (this.type) {
        options.type = this.type;
      }
      if (this.projectId) {
        options.project = this.projectId;
      }
      userCommentsStore.getThreads(this.url, options);
    },

    executeRecaptcha() {
      if (this.recaptcha.isExecuting) {
        return Promise.reject(new Error('reCAPTCHA is already running'));
      }

      this.recaptcha.isExecuting = true;

      return new Promise((resolve, reject) => {
        this.recaptcha.pendingResolver = resolve;
        this.recaptcha.pendingRejecter = reject;
        this.$refs.recaptcha.execute();
      });
    },

    resetRecaptcha() {
      this.recaptcha.pendingResolver = null;
      this.recaptcha.pendingRejecter = null;
      this.recaptcha.isExecuting = false;
      this.$refs.recaptcha.reset();
    },

    onCaptchaVerified(recaptchaToken) {
      if (this.recaptcha.pendingResolver) {
        this.recaptcha.pendingResolver(recaptchaToken);
      }
      this.resetRecaptcha();
    },

    onCaptchaExpired() {
      if (this.recaptcha.pendingRejecter) {
        this.recaptcha.pendingRejecter(new Error('reCAPTCHA expired'));
      }
      this.resetRecaptcha();
    },

    onCaptchaError() {
      if (this.recaptcha.pendingRejecter) {
        this.recaptcha.pendingRejecter(new Error('reCAPTCHA error occurred'));
      }
      this.resetRecaptcha();
    }
  }
};
</script>

<style>
.grecaptcha-badge {
  visibility: hidden;
}
</style>
